import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userApi } from '@features/users/api/user.api';
import { planKeys } from '@features/plans/model/use-plans';
import type {
  AssignSubscriptionInput,
  ListUsersParams,
} from '@features/users/model/user.types';

/**
 * Query keys in one place, so invalidating "all user lists" cannot go stale as
 * new parameters are added.
 */
export const userKeys = {
  all: ['users'] as const,
  list: (params: ListUsersParams) => [...userKeys.all, 'list', params] as const,
  statuses: () => [...userKeys.all, 'statuses'] as const,
  plans: () => [...userKeys.all, 'plans'] as const,
};

export function useUsers(params: ListUsersParams) {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => userApi.list(params),
    // Keeps the current rows on screen while the next page loads, instead of
    // collapsing the table to a spinner on every paging click.
    placeholderData: keepPreviousData,
  });
}

export function useSubscriptionStatuses() {
  return useQuery({
    queryKey: userKeys.statuses(),
    queryFn: userApi.statuses,
    // The set of statuses in use barely changes.
    staleTime: 5 * 60_000,
  });
}

export function useSubscriptionPlans() {
  return useQuery({
    queryKey: userKeys.plans(),
    queryFn: userApi.plans,
    staleTime: 5 * 60_000,
  });
}

/**
 * Assigns a plan to a user.
 *
 * Every user list is invalidated rather than just the current one: the new
 * subscription changes which plan and status filters the user falls under, so a
 * cached list under different filters is stale too. The status list can also
 * gain a value that was not in use before.
 */
export function useAssignSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, ...input }: AssignSubscriptionInput & { userId: string }) =>
      userApi.assignSubscription(userId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
      void queryClient.invalidateQueries({ queryKey: planKeys.all });
    },
  });
}
